import React from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import Seo, { useLocalBusinessLd } from "@/components/Seo";
import { useContent } from "@/hooks/useContent";
import { OWNER_TEXTS } from "@/lib/contentDefaults";
import { Camera, Megaphone, LineChart, MessageSquare, KeyRound, Sparkles, Shirt, ClipboardCheck, HandHeart, ArrowRight } from "lucide-react";

const HERO = "https://images.pexels.com/photos/6474532/pexels-photo-6474532.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1600";

const services = [
  { icon: Camera, title: "Photographies & annonces", d: "Création et optimisation d'annonces professionnelles avec reportage photo soigné." },
  { icon: Megaphone, title: "Diffusion multi-plateformes", d: "Diffusion et gestion de vos annonces sur Airbnb, Booking.com et en réservation directe." },
  { icon: LineChart, title: "Optimisation tarifaire", d: "Tarification dynamique pour maximiser vos revenus tout au long de l'année." },
  { icon: MessageSquare, title: "Communication voyageurs", d: "Réponses rapides, filtrage des demandes et accompagnement des voyageurs 7j/7." },
  { icon: KeyRound, title: "Organisation des arrivées", d: "Gestion des check-in / check-out, remise des clés et états des lieux." },
  { icon: Sparkles, title: "Ménage & linge", d: "Ménage professionnel entre chaque séjour, linge fourni." },
  { icon: Shirt, title: "Linge & consommables", d: "Fourniture et entretien du linge de maison et produits d'accueil." },
  { icon: ClipboardCheck, title: "Suivi du logement", d: "Contrôle régulier de l'état du logement et petites interventions." },
  { icon: HandHeart, title: "Accompagnement propriétaire", d: "Un interlocuteur dédié et un reporting clair sur vos revenus." },
];

const steps = [
  { n: "01", t: "Estimation gratuite", d: "Nous étudions votre logement et estimons son potentiel de revenus." },
  { n: "02", t: "Mise en valeur", d: "Photos, annonce optimisée et mise en ligne sur toutes les plateformes." },
  { n: "03", t: "Gestion complète", d: "Nous gérons réservations, voyageurs, ménage et linge." },
  { n: "04", t: "Vous percevez vos revenus", d: "Reporting transparent et reversements réguliers." },
];

export default function Concierge() {
  const c = useContent("owner_texts", OWNER_TEXTS);
  return (
    <Layout>
      <Seo title="Conciergerie à Rouen | Nos services | NJ Conciergerie"
        description="Conciergerie à Rouen : photos, annonces, diffusion Airbnb & Booking, tarification, accueil voyageurs, ménage et linge. Gestion complète."
        canonical="/conciergerie" jsonLd={useLocalBusinessLd()} />
      <section className="relative h-[46vh] flex items-center">
        <img src={HERO} alt="Conciergerie Rouen" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-nj-dark/55" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <span className="text-nj-gold text-xs tracking-[0.28em] uppercase font-semibold">{c.concierge_hero_eyebrow}</span>
          <h1 className="font-serif font-light text-white text-4xl sm:text-5xl lg:text-6xl mt-3 max-w-2xl">{c.concierge_hero_title}</h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-2xl">
          <span className="text-nj-gold text-xs tracking-[0.28em] uppercase font-semibold">Nos services</span>
          <h2 className="font-serif font-light text-3xl sm:text-4xl text-nj-text mt-3">{c.concierge_services_title}</h2>
          <p className="text-nj-text2 mt-3">{c.concierge_services_intro}</p>
        </div>
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 mt-12">
          {services.map((s) => (
            <div key={s.title} className="bg-white border border-nj-olive rounded-md p-7 hover:shadow-md transition-shadow">
              <div className="w-11 h-11 rounded-sm bg-nj-olive flex items-center justify-center mb-4">
                <s.icon className="w-5 h-5 text-nj-primary" />
              </div>
              <h3 className="font-serif text-xl text-nj-text">{s.title}</h3>
              <p className="text-sm text-nj-text2 mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-nj-olive/40 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-nj-gold text-xs tracking-[0.28em] uppercase font-semibold">Notre fonctionnement</span>
          <h2 className="font-serif font-light text-3xl sm:text-4xl text-nj-text mt-3 mb-12">{c.concierge_steps_title}</h2>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((s) => (
              <div key={s.n}>
                <div className="font-serif text-5xl text-nj-gold">{s.n}</div>
                <h3 className="font-serif text-xl text-nj-text mt-3">{s.t}</h3>
                <p className="text-sm text-nj-text2 mt-2 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <h2 className="font-serif font-light text-3xl sm:text-4xl text-nj-text">{c.concierge_cta_title}</h2>
        <p className="text-nj-text2 mt-3">{c.concierge_cta_subtitle}</p>
        <div className="flex flex-wrap gap-3 justify-center mt-7">
          <Link to="/confier-mon-logement" className="bg-nj-primary text-white font-semibold px-7 py-3.5 rounded-sm hover:bg-nj-primaryHover transition-colors inline-flex items-center gap-2">
            Demander une estimation <ArrowRight className="w-4 h-4" />
          </Link>
          <a href="tel:+33666934267" className="border border-nj-primary text-nj-primary font-semibold px-7 py-3.5 rounded-sm hover:bg-nj-olive transition-colors">Être rappelé</a>
        </div>
      </section>
    </Layout>
  );
}
